/**
 * Storage Module Configuration — Types & Pure Functions
 *
 * No process.env reads, no raw Knex, no singletons.
 * Config is read once at init() via CollectionService (envDefaults applied by runtime).
 */

import { join, isAbsolute } from 'node:path'
import { existsSync, mkdirSync } from 'node:fs'

// ============================================================================
// Interfaces
// ============================================================================

export interface ResizeConfig {
  /** Max resize width (default: 2048) */
  maxWidth: number
  /** Max resize height (default: 2048) */
  maxHeight: number
  /** Default JPEG/WebP quality (default: 80) */
  defaultQuality: number
  /** Cache directory for resized images */
  cachePath: string
}

export interface VideoConfig {
  /** Max thumbnail width (default: 1280) */
  maxWidth: number
  /** Max thumbnail height (default: 720) */
  maxHeight: number
  /** Default JPEG quality for thumbnails (default: 80) */
  defaultQuality: number
  /** Max preview duration in seconds (default: 5) */
  maxPreviewDuration: number
  /** Max transcode file size in bytes (default: 500MB) */
  maxTranscodeSize: number
  /** Cache directory for video outputs */
  cachePath: string
}

export interface StorageConfig {
  /** Configuration scope (unique identifier) */
  scope: string

  /** Storage driver */
  driver: 'filesystem' | 's3'

  /** Base path for filesystem (default: ./storage) */
  basePath: string

  /** Base public URL for generating file URLs */
  baseUrl?: string

  /** Maximum file size in bytes (default: 10MB) */
  maxFileSize: number

  /** Globally allowed MIME types (optional) */
  allowedMimeTypes?: string[]

  /** ID generator (injected from ctx.helpers) */
  generateId: () => string

  /** Dynamic image resize configuration */
  resize: ResizeConfig

  /** Video processing configuration */
  video: VideoConfig

  /** S3 configuration */
  s3?: {
    bucket: string
    region: string
    accessKeyId: string
    secretAccessKey: string
    /** Custom endpoint for MinIO/other providers */
    endpoint?: string
  }
}

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024 // 10MB

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Resolves a storage path:
 * - Absolute paths: used directly
 * - Relative paths (./storage): resolved to projectPath/data/{path}
 */
export function resolveStoragePath(path: string, projectPath: string): string {
  if (isAbsolute(path)) {
    return path
  }
  const cleanPath = path.startsWith('./') ? path.slice(2) : path
  return join(projectPath, 'data', cleanPath)
}

/**
 * Builds a full StorageConfig from a DB row (CollectionService record).
 * Pure function — no env reads, no DB access.
 */
export function buildStorageConfig(
  row: Record<string, unknown> | null | undefined,
  projectPath: string,
  generateId: () => string
): StorageConfig {
  const driver = ((row?.['driver'] as string) || 'filesystem') as 'filesystem' | 's3'
  const scope = (row?.['scope'] as string) || 'default'

  const basePath = driver === 'filesystem'
    ? resolveStoragePath((row?.['base_path'] as string) || './storage', projectPath)
    : ''

  // Ensure filesystem directory exists
  if (driver === 'filesystem' && basePath && !existsSync(basePath)) {
    mkdirSync(basePath, { recursive: true })
  }

  const cachePath = resolveStoragePath('./storage-cache', projectPath)
  const videoCachePath = resolveStoragePath('./video-cache', projectPath)

  const config: StorageConfig = {
    scope,
    driver,
    basePath,
    baseUrl: (row?.['base_url'] as string) || undefined,
    maxFileSize: (row?.['max_file_size'] as number) || DEFAULT_MAX_SIZE,
    allowedMimeTypes: (row?.['allowed_mime_types'] as string)?.split(',').map(t => t.trim()),
    generateId,
    resize: {
      maxWidth: (row?.['resize_max_width'] as number) || 2048,
      maxHeight: (row?.['resize_max_height'] as number) || 2048,
      defaultQuality: (row?.['resize_quality'] as number) || 80,
      cachePath
    },
    video: {
      maxWidth: (row?.['video_max_width'] as number) || 1280,
      maxHeight: (row?.['video_max_height'] as number) || 720,
      defaultQuality: (row?.['video_quality'] as number) || 80,
      maxPreviewDuration: (row?.['video_max_preview'] as number) || 5,
      maxTranscodeSize: (row?.['video_max_transcode'] as number) || 500 * 1024 * 1024,
      cachePath: videoCachePath
    }
  }

  // S3 config
  if (driver === 's3') {
    const bucket = row?.['bucket'] as string
    const region = row?.['region'] as string
    const accessKeyId = row?.['access_key_id'] as string
    const secretAccessKey = row?.['secret_access_key'] as string

    if (bucket && region && accessKeyId && secretAccessKey) {
      config.s3 = {
        bucket,
        region,
        accessKeyId,
        secretAccessKey,
        endpoint: (row?.['endpoint'] as string) || undefined
      }
    }
  }

  return config
}
